import { Link } from 'react-router-dom';
import { CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { Module, Enrollment } from '../../types/elearning';
import Badge from '../ui/Badge';

interface ModuleListProps {
  modules: Module[];
  courseSlug: string;
  enrollment?: Enrollment;
  forceLocked?: boolean;
}

export default function ModuleList({ modules, courseSlug, enrollment, forceLocked = false }: ModuleListProps) {
  const completedModuleIds = new Set(
    enrollment?.moduleCompletions?.map((mc) => mc.moduleId) || []
  );

  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getModuleStatus = (module: Module) => {
    if (!enrollment) return 'locked';
    if (completedModuleIds.has(module.id)) return 'completed';
    return 'available';
  };

  return (
    <div className="space-y-3">
      {modules.map((module, index) => {
        const status = forceLocked ? 'locked' : getModuleStatus(module);
        const isLocked = status === 'locked';
        const isCompleted = status === 'completed';

        const ModuleContent = (
          <div className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center font-semibold text-gray-600">
                {index + 1}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 mb-2">
                <h3
                  className={`text-lg font-semibold ${
                    isLocked ? 'text-gray-400' : 'text-gray-900'
                  }`}
                >
                  {module.title}
                </h3>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isCompleted && (
                    <CheckCircleIcon className="h-6 w-6 text-green-500" />
                  )}
                  {isLocked && <LockClosedIcon className="h-5 w-5 text-gray-400" />}
                  {module.hasQuiz && (
                    <Badge variant="default" className="bg-blue-100 text-blue-700 text-xs">
                      Quiz
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>{formatTime(module.estimatedTime)}</span>
                {isCompleted && (
                  <span className="text-green-600 font-medium">Completed</span>
                )}
              </div>
            </div>
          </div>
        );

        if (isLocked) {
          return (
            <div key={module.id} className="opacity-60">
              {ModuleContent}
            </div>
          );
        }

        return (
          <Link
            key={module.id}
            to={`/elearning/courses/${courseSlug}/modules/${module.slug}`}
            className="block"
          >
            {ModuleContent}
          </Link>
        );
      })}
    </div>
  );
}

